import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Label, TextField } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { UseLoginResult } from './useLogin';

type LoginViewProps = UseLoginResult;

export const LoginView: React.FC<LoginViewProps> = ({
    email,
    password,
    hasError,
    isSubmitting,
    isValid,
    onEmailChange,
    onPasswordChange,
    onSubmit,
}) => {
    const { t } = useTranslation('pages');

    return (
        <main
            className="flex min-h-screen w-full items-center justify-center bg-background px-4 py-10"
            {...getNodeTestIdProp('login-page')}
        >
            <Card
                className="w-full max-w-md"
                {...getNodeTestIdProp('login-card')}
            >
                <Card.Header
                    className="flex flex-col gap-1"
                    {...getNodeTestIdProp('login-card-header')}
                >
                    <h1
                        className="text-2xl font-semibold text-foreground"
                        {...getNodeTestIdProp('login-title')}
                    >
                        {t('login.title')}
                    </h1>
                    <p
                        className="text-sm text-muted"
                        {...getNodeTestIdProp('login-subtitle')}
                    >
                        {t('login.subtitle')}
                    </p>
                </Card.Header>
                <Card.Content {...getNodeTestIdProp('login-card-content')}>
                    <form
                        className="flex flex-col gap-5"
                        noValidate
                        onSubmit={(event) => {
                            event.preventDefault();
                            onSubmit();
                        }}
                        {...getNodeTestIdProp('login-form')}
                    >
                        <TextField
                            autoComplete="email"
                            fullWidth
                            isRequired
                            onChange={onEmailChange}
                            type="email"
                            value={email}
                            {...getNodeTestIdProp('login-email')}
                        >
                            <Label
                                id="login-email-label"
                                {...getNodeTestIdProp('login-email-label')}
                            >
                                {t('login.email.label')}
                            </Label>
                            <Input
                                placeholder={t('login.email.placeholder')}
                                {...getNodeTestIdProp('login-email-input')}
                            />
                        </TextField>
                        <TextField
                            autoComplete="current-password"
                            fullWidth
                            isRequired
                            onChange={onPasswordChange}
                            type="password"
                            value={password}
                            {...getNodeTestIdProp('login-password')}
                        >
                            <Label
                                id="login-password-label"
                                {...getNodeTestIdProp('login-password-label')}
                            >
                                {t('login.password.label')}
                            </Label>
                            <Input
                                placeholder={t('login.password.placeholder')}
                                type="password"
                                {...getNodeTestIdProp('login-password-input')}
                            />
                        </TextField>
                        {hasError ? (
                            <p
                                className="text-sm text-danger"
                                role="alert"
                                {...getNodeTestIdProp('login-error')}
                            >
                                {t('login.error.invalidCredentials')}
                            </p>
                        ) : null}
                        <Button
                            isDisabled={!isValid}
                            isPending={isSubmitting}
                            type="submit"
                            variant="primary"
                            {...getNodeTestIdProp('login-submit')}
                        >
                            {t('login.submit')}
                        </Button>
                    </form>
                </Card.Content>
            </Card>
        </main>
    );
};
